import { QuestionAnswerMap } from '@care-portals/api-interfaces';
import { GenericTerminalQuestionFieldAnswers } from '../../Generic/questions';

export const MODAFINIL_THERAPY_CONSENT = `You are requesting treatment with modafinil, a wakefulness-promoting medication that may be used to improve excessive sleepiness, alertness, and daytime functioning. Modafinil is a Schedule IV controlled substance. It is FDA-approved for excessive sleepiness associated with narcolepsy, obstructive sleep apnea, and shift work sleep disorder. When used for idiopathic hypersomnia, cognitive enhancement, focus, concentration, productivity, brain fog, mental fatigue, or another condition not listed in the FDA-approved labeling, treatment is considered off-label. Off-label prescribing is a common and accepted medical practice when clinically appropriate and based on medical judgment.

Modafinil promotes wakefulness but does not replace adequate sleep or treat the underlying cause of sleepiness. If you have obstructive sleep apnea, modafinil does not treat the airway obstruction and does not replace CPAP, APAP, BiPAP, an oral appliance, or another prescribed sleep apnea treatment. Treatment of the underlying sleep disorder should be continued, and additional evaluation may be required if your symptoms may be caused by insufficient sleep, another sleep disorder, a medical or psychiatric condition, medication side effects, or substance use.

Potential benefits may include reduced excessive daytime sleepiness, improved ability to remain awake during intended waking hours, improved alertness during work or daily activities, and improved concentration or mental clarity when these symptoms are related to sleepiness. Benefits are not guaranteed, and modafinil may not completely eliminate sleepiness or return your level of alertness to normal. Taking modafinil does not establish that you are safe to drive, operate machinery, or perform other hazardous or safety-sensitive activities.

Common side effects include headache, nausea, nervousness, anxiety, insomnia, dizziness, diarrhea, decreased appetite, dry mouth, indigestion, palpitations, and increased blood pressure or heart rate. Less common but serious risks include chest pain, abnormal heart rhythm, fainting, severe psychiatric symptoms, suicidal thoughts, mania, hallucinations, paranoia, psychosis, serious allergic reactions, and severe skin reactions such as Stevens-Johnson syndrome, toxic epidermal necrolysis, DRESS syndrome, or multi-organ hypersensitivity. You should stop taking modafinil and promptly seek medical advice if you develop a new rash. Seek emergency medical care for blistering or peeling skin, sores in the mouth or eyes, swelling of the face, lips, tongue, or throat, difficulty breathing, chest pain, fainting, severe palpitations, hallucinations, severe mood changes, or suicidal thoughts.

Modafinil should not be used if you have had an allergic or hypersensitivity reaction to modafinil, armodafinil, Provigil, or Nuvigil. Additional evaluation may be required if you have uncontrolled high blood pressure, heart disease, arrhythmia, chest pain, fainting, cardiomyopathy, a recent heart attack, severe liver disease, seizures, severe anxiety, bipolar disorder, mania, psychosis, suicidal thoughts, or a history of serious medication rash. Your clinician may require blood pressure and heart rate measurements, medical records, laboratory testing, an electrocardiogram, pregnancy testing, or specialist clearance before or during treatment.

Modafinil may cause fetal harm and is generally not recommended during pregnancy. You must inform your clinician if you are pregnant, may be pregnant, are trying to become pregnant, or are breastfeeding. Modafinil may reduce the effectiveness of hormonal contraceptives, including birth control pills, patches, rings, injections, and implants. If you use hormonal contraception, you should use an effective alternative or additional nonhormonal contraceptive method during treatment and for at least one month after your last dose. Contact your clinician promptly if pregnancy occurs.

Modafinil may interact with hormonal contraceptives, warfarin, cyclosporine, tacrolimus, seizure medications, certain antidepressants and psychiatric medications, sedatives, opioids, HIV or hepatitis medications, cancer medications, and other stimulants or wakefulness-promoting medications. You must provide a complete list of all prescription medications, controlled substances, over-the-counter medications, supplements, herbal products, nootropics, alcohol, cannabis, and other substances you use. Do not combine modafinil with another stimulant or wakefulness-promoting medication unless specifically instructed by your clinician.

Because modafinil is a controlled substance, it carries a risk of misuse, abuse, dependence, and diversion. You must take it only as prescribed and may not increase the dose or change the timing without approval. You must not share, sell, trade, or give the medication to another person. You may be required to use one pharmacy, complete prescription monitoring checks, attend follow-up visits, sign a controlled-substance agreement, or complete drug screening when clinically appropriate. Early refills or replacement of lost, stolen, damaged, or misused medication are not guaranteed. Treatment may be discontinued if there is evidence of misuse, diversion, unsafe combinations, inaccurate information, failure to complete monitoring, or lack of meaningful clinical benefit.

Ongoing follow-up is required to monitor your response to treatment and assess for side effects, blood pressure or heart rate changes, sleep quality, mood or behavioral changes, continued sleepiness, medication interactions, and safe use. Your dose may be adjusted based on clinical response and tolerability. You must continue to avoid driving, operating machinery, or performing hazardous tasks whenever you feel sleepy or are unsure how the medication affects you.

Please attest to the following confirming that all information you have provided to us is true and complete. I verify that I am the patient and that I have answered the questions asked in this intake form. I confirm that I have reviewed and understood all the questions asked of me. I attest that the answers and information I have provided in this questionnaire are true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. I will not hold the doctor or affiliated medical practice responsible for any oversights or omissions, whether intentional or not, in the information that I provided.

I verify that I am the patient and that I have answered the questions asked in this intake form.  I confirm that I have reviewed and understood all the questions asked of me.  I attest that the answers and information I have provided in this questionnaire is true and complete to the best of my knowledge. I understand that it is critical to my health to share complete health information with my doctor. 

By proceeding with treatment, you acknowledge whether modafinil is being prescribed for an FDA-approved or off-label indication. You understand the potential benefits, limitations, risks, side effects, controlled-substance requirements, medication interactions, pregnancy-related precautions, and driving and safety considerations associated with modafinil therapy. You agree to take the medication only as prescribed, continue treatment of any underlying sleep disorder, attend required follow-up, complete requested monitoring, and promptly report any concerning symptoms or side effects.
`;

const modafinilQuestions = [
  {
    field: 'modafinil_controlledSubstanceConsent',
    label:
      'The medication that you have requested contains modafinil which is a Schedule IV medication with a low but potential risk of misuse, dependence, and addiction. It may NOT be appropriate for individuals with a history of substance use disorder or stimulant misuse. By proceeding, I confirm that I have disclosed any history of addiction and agree to take this medication only as prescribed. I understand that misuse or dose changes without approval may result in discontinuation of treatment. If I am requesting this medication for treatment of sleep apnea or narcolepsy, I understand that I should have my medical records available to upload.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I prefer not to proceed with treatment',
          class: 'option-decline',
          key: 'do_not_proceed'
        },
        {
          value:
            'I have no history of substance use disorder or stimulant misuse and wish to proceed',
          class: 'option-consent',
          key: 'proceed'
        }
      ]
    }
  },
  {
    field: 'modafinil_mainSymptom',
    label: 'Which best describes your main symptom? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'I feel sleepy and may fall asleep unintentionally',
          key: 'sleepy'
        },
        {
          value: 'I feel fatigued, drained, or low-energy',
          key: 'fatigued'
        },
        {
          value: 'I feel mentally foggy or slowed down',
          key: 'brain_fog'
        },
        {
          value: 'I have trouble focusing or completing tasks',
          key: 'focus_difficulty'
        }
      ]
    }
  },
  {
    field: 'modafinil_sleepConditions',
    label: 'Do you have any of the following? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value:
            'I have sleepiness or insomnia related to shift work or irregular hours.',
          key: 'shift_work'
        },
        {
          value:
            'I have obstructive sleep apnea and still feel excessively sleepy during the day',
          key: 'sleep_apnea'
        },
        {
          value: 'I have diagnosed narcolepsy',
          key: 'narcolepsy'
        },
        {
          value:
            'I sleep excessively or feel persistently sleepy despite adequate sleep',
          key: 'hypersomnia'
        },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
{
    field: 'modafinil_sleepConditions2',
    label: 'Do you have any of the following? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value:
            'I have sleepiness or insomnia related to shift work or irregular hours.',
          key: 'shift_work'
        },
        {
          value:
            'I have obstructive sleep apnea and still feel excessively sleepy during the day',
          key: 'sleep_apnea'
        },
        {
          value: 'I have diagnosed narcolepsy',
          key: 'narcolepsy'
        },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'modafinil_shiftsPerMonth',
    label: 'How many shifts on average do you work per month?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: '1-5', key: '1_to_5' },
        { value: '6-10', key: '6_to_10' },
        { value: '11-20', key: '11_to_20' },
        { value: '21-30', key: '21_to_30' }
      ]
    }
  },
  {
    field: 'modafinil_sleepApneaTreated',
    label: 'Is your sleep apnea treated?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: "Yes I'm on CPAP, BiPAP, or an oral device",
          key: 'treated_device'
        },
        {
          value: 'Yes this has resolved through weight loss or surgery',
          key: 'resolved'
        },
        {
          value: "No I'm not able to tolerate or afford treatment",
          key: 'untreated'
        }
      ]
    }
  },
  {
    field: 'modafinil_sleepApneaAcknowledgment',
    label:
      'Modafinil may help reduce excessive daytime sleepiness associated with obstructive sleep apnea, but it does not treat the underlying airway obstruction. It is important to continue or pursue appropriate sleep apnea treatment, such as CPAP, an oral appliance, or another therapy recommended by your clinician.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        {
          value: "I understand, let's move forward",
          class: 'option-consent',
          key: 'understood'
        }
      ]
    }
  },
  {
    field: 'modafinil_sleepApneaRecordsUpload',
    label:
      'Please upload a copy of your medical records or sleep study results which show your diagnosis of sleep apnea.',
    type: 'file_upload',
    accepts: ['application/pdf', 'image/png', 'image/jpeg'],
    answer: [
      {
        resourceId: 'modafinil_sleepApneaRecordsUpload',
        buttonLabel: 'Upload Document'
      }
    ]
  },
  {
    field: 'modafinil_narcolepsyRecordsUpload',
    label:
      'Please upload a copy of your medical records or sleep study results which show your diagnosis of narcolepsy.',
    type: 'file_upload',
    accepts: ['application/pdf', 'image/png', 'image/jpeg'],
    answer: [
      {
        resourceId: 'modafinil_narcolepsyRecordsUpload',
        buttonLabel: 'Upload Document'
      }
    ]
  },
  {
    field: 'modafinil_symptomDuration',
    label: 'How long has this been a problem?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Less than 1 month', key: 'less_than_1_month' },
        { value: '1–6 months', key: '1_to_6_months' },
        { value: '7-12 months', key: '7_to_12_months' },
        { value: 'More than 1 year', key: 'more_than_1_year' },
        { value: 'Lifelong', key: 'lifelong' }
      ]
    }
  },
  {
    field: 'modafinil_symptomFrequency',
    label: 'How often does this occur?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Daily', key: 'daily' },
        { value: 'Most days', key: 'most_days' },
        { value: 'Several days per week', key: 'several_days_per_week' },
        { value: 'Weekly', key: 'weekly' },
        { value: 'A few times per month', key: 'few_times_per_month' },
        { value: 'Rarely', key: 'rarely' }
      ]
    }
  },
  {
    field: 'modafinil_functionalImpact',
    label: 'How much does this affect your functioning?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Mild impact', key: 'mild' },
        { value: 'Moderate impact', key: 'moderate' },
        { value: 'Severe impact', key: 'severe' }
      ]
    }
  },
  {
    field: 'modafinil_affectedAreas',
    label: 'Which areas are affected? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Work', key: 'work' },
        { value: 'School', key: 'school' },
        { value: 'Driving', key: 'driving' },
        { value: 'Parenting or caregiving', key: 'parenting_caregiving' },
        {
          value: 'Household responsibilities',
          key: 'household_responsibilities'
        },
        { value: 'Social functioning', key: 'social_functioning' },
        { value: 'Relationships', key: 'relationships' },
        { value: 'Exercise', key: 'exercise' },
        { value: 'Safety-sensitive tasks', key: 'safety_sensitive_tasks' },
        { value: 'Other', key: 'other' },
        { value: 'None', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'modafinil_affectedAreasOther',
    label: 'Tell us more about the other affected areas of your life',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'modafinil_affectedAreasOther'
    }
  },
  {
    field: 'modafinil_riskConditions',
    label:
      'Do any of the following conditions or medication risks apply to you? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value:
            'I have had an allergic reaction to modafinil, Provigil, armodafinil, or Nuvigil',
          key: 'allergic_reaction'
        },
        {
          value:
            'I have uncontrolled high blood pressure, irregular heartbeat, or unexplained fainting',
          key: 'uncontrolled_bp_or_arrhythmia'
        },
        {
          value:
            'I have known heart disease including left ventricular hypertrophy (LVH) or mitral valve prolapse (MVP)',
          key: 'heart_disease'
        },
        {
          value: 'I have had Stevens-Johnson Syndrome',
          key: 'stevens_johnson'
        },
        {
          value:
            'I have a history of mania, psychosis, hallucinations, paranoia, severe agitation, severe uncontrolled anxiety, or suicidal thoughts',
          key: 'psychiatric_history'
        },
        {
          value: 'I have severe liver disease or cirrhosis',
          key: 'liver_disease'
        },
        {
          value: 'I have a seizure disorder',
          key: 'seizure_disorder'
        },
        {
          value: 'I have a history of stroke or recent head trauma',
          key: 'stroke_or_head_trauma'
        },
        {
          value:
            'I currently take an MAO inhibitor, another stimulant, a weight-loss stimulant, or another wakefulness-promoting medication',
          key: 'interacting_medication'
        },
        {
          value:
            'I previously experienced a serious or intolerable reaction to a stimulant or wakefulness-promoting medication',
          key: 'prior_stimulant_reaction'
        },
        {
          value: 'I have known dementia',
          key: 'dementia'
        },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'modafinil_sleepScheduleConsistency',
    label: 'How consistent is your sleep schedule?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Very consistent', key: 'very_consistent' },
        { value: 'Somewhat consistent', key: 'somewhat_consistent' },
        { value: 'Highly variable', key: 'highly_variable' },
        {
          value: 'Depends on work or caregiving schedule',
          key: 'depends_on_schedule'
        },
        { value: 'Unsure', key: 'unsure' }
      ]
    }
  },
  {
    field: 'modafinil_sleepHours',
    label: 'How many total hours do you usually sleep at night?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Less than 4 hours', key: 'less_than_4' },
        { value: '4–6 hours', key: '4_to_6' },
        { value: '6–8 hours', key: '6_to_8' },
        { value: '8–10 hours', key: '8_to_10' },
        { value: '10–12 hours', key: '10_to_12' },
        { value: 'More than 12 hours', key: 'more_than_12' },
        { value: 'Variable or irregular', key: 'variable' }
      ]
    }
  },
  {
    field: 'modafinil_sleepSymptoms',
    label: 'Do any of the following apply to you? (Select all that apply)',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        {
          value: 'I snore loudly, stop breathing, gasp, or choke during sleep',
          key: 'snoring_or_apnea'
        },
        {
          value: 'I wake with morning headaches, dry mouth, or sore throat',
          key: 'morning_symptoms'
        },
        {
          value:
            'I have an uncomfortable urge to move my legs when resting',
          key: 'restless_legs'
        },
        {
          value: 'I have trouble falling or staying asleep',
          key: 'insomnia'
        },
        {
          value: 'I do not feel rested when I wake up',
          key: 'unrefreshing_sleep'
        },
        {
          value: 'None of the above',
          key: 'none',
          uncheckOthers: true
        }
      ]
    }
  },
  {
    field: 'modafinil_sedatingMedications',
    label: 'Do you take medications that may cause sleepiness or fatigue?',
    type: 'select',
    answer: {
      type: 'multi_select',
      options: [
        { value: 'Benzodiazepines', key: 'benzodiazepines' },
        { value: 'Antihistamines', key: 'antihistamines' },
        { value: 'Opioids', key: 'opioids' },
        { value: 'Antidepressants', key: 'antidepressants' },
        { value: 'Antipsychotics', key: 'antipsychotics' },
        { value: 'Antiseizure medications', key: 'antiseizure' },
        { value: 'Alcohol or cannabis', key: 'alcohol_or_cannabis' },
        { value: 'None of the above', key: 'none', uncheckOthers: true }
      ]
    }
  },
  {
    field: 'modafinil_phq2Interest',
    label:
      'Over the last 2 weeks, how often have you had little interest or pleasure in doing things?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Not at all', key: 'not_at_all' },
        { value: 'Several days', key: 'several_days' },
        { value: 'More than half the days', key: 'more_than_half_the_days' },
        { value: 'Nearly every day', key: 'nearly_every_day' }
      ]
    }
  },
  {
    field: 'modafinil_phq2Mood',
    label:
      'Over the last 2 weeks, how often have you felt down, depressed, or hopeless?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Not at all', key: 'not_at_all' },
        { value: 'Several days', key: 'several_days' },
        { value: 'More than half the days', key: 'more_than_half_the_days' },
        { value: 'Nearly every day', key: 'nearly_every_day' }
      ]
    }
  },
  {
    field: 'modafinil_priorUse',
    label: 'Have you ever used modafinil before?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'modafinil_priorSideEffects',
    label: 'Did you experience side effects from modafinil?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'modafinil_priorSideEffectsDetails',
    label: 'Please provide specific information about your side effects',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'modafinil_priorSideEffectsDetails'
    }
  },
  {
    field: 'modafinil_bloodPressure',
    label:
      'Blood pressure monitoring is important while on modafinil. What was your most recent blood pressure reading? Please select the closest range. If your top and bottom numbers fall into different ranges, choose the higher range.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Low: Less than 90/60', key: 'low' },
        {
          value: 'Normal or mildly elevated: Less than 130/80',
          key: 'normal'
        },
        {
          value: 'High: 130–159 (top number) or 80–99 (bottom number)',
          key: 'high'
        },
        {
          value:
            'Very high: 160–179 (top number) or 100–119 (bottom number)',
          key: 'very_high'
        },
        {
          value:
            'Severely high: 180 or higher (top number) or 120 or higher (bottom number)',
          key: 'severely_high'
        }
      ]
    }
  },
  {
    field: 'modafinil_heartRate',
    label:
      'Pulse-rate monitoring is important while taking modafinil. What was your most recent resting heart rate? Please select the closest range. Measure your pulse while seated, calm, and at rest.',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Low: Less than 50 beats per minute', key: 'low' },
        { value: 'Mildly low: 50–59 beats per minute', key: 'mildly_low' },
        { value: 'Normal: 60–100 beats per minute', key: 'normal' },
        { value: 'High: 101 beats per minute or more', key: 'high' }
      ]
    }
  },
  {
    field: 'modafinil_sexAssignedAtBirth',
    label: 'What was your sex assigned at birth?',
    extras: { sendToBeluga: false },
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Male', key: 'male' },
        { value: 'Female', key: 'female' }
      ]
    }
  },
  {
    field: 'modafinil_pregnancyStatus',
    label:
      'Are you currently pregnant, breastfeeding or planning to become pregnant?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
  {
    field: 'modafinil_pregnancyConsent',
    label: 'Consent (pregnancy):',
    type: 'select',
    educationalSlide: `I understand that the medication prescribed to me by my healthcare provider may not be safe to take during pregnancy. I acknowledge that taking this medication while pregnant could pose risks to my health and the health of a developing fetus.

I agree to take necessary precautions to avoid becoming pregnant while using this medication, including the use of effective contraception methods as discussed with my healthcare provider.

Modafinil may reduce the effectiveness of hormonal birth control, increasing the risk of unintended pregnancy. Patients using birth control pills, patches, vaginal rings, injections, implants, or hormonal IUDs should use an additional or alternative nonhormonal contraceptive method while taking modafinil and for one month after the last dose. Patients should discuss the most appropriate contraceptive option with their healthcare provider.

By selecting below, I confirm that I have read and understand the information provided above. I consent to proceed with the treatment under these conditions.`,
    answer: {
      type: 'single_select',
      options: [
        {
          value: 'I have read the information and do NOT wish to proceed',
          class: 'option-decline',
          key: 'non_consent'
        },
        {
          value:
            'I have read and understand the above information, I understand the risks and wish to proceed',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  },
  {
    field: 'modafinil_supportingDocuments',
    label:
      'Do you have any supporting documents to upload for your provider to review?',
    type: 'select',
    answer: {
      type: 'single_select',
      options: [
        { value: 'Yes', key: 'yes' },
        { value: 'No', key: 'no' }
      ]
    }
  },
{
    field: 'modafinil_supportingDocumentsUpload2',
    label:
      'Please upload a copy of your medical records, sleep study results, or work schedule to help the provider confirm your diagnosis or work schedule.',
    type: 'file_upload',
    accepts: ['application/pdf', 'image/png', 'image/jpeg'],
    answer: [
      {
        resourceId: 'modafinil_supportingDocumentsUpload',
        buttonLabel: 'Upload Document'
      }
    ]
  },
  {
    field: 'modafinil_supportingDocumentsUpload',
    label:
      'Please upload that document here such as sleep data and medical records',
    type: 'file_upload',
    accepts: ['application/pdf', 'image/png', 'image/jpeg'],
    answer: [
      {
        resourceId: 'modafinil_supportingDocumentsUpload',
        buttonLabel: 'Upload Document'
      }
    ]
  },
  {
    field: 'modafinil_additionalQuestions',
    label: 'What other information or questions do you have for the doctor?',
    type: 'info',
    answer: {
      type: 'text',
      rows: 4,
      name: 'modafinil_additionalQuestions'
    }
  },
  {
    field: 'modafinil_treatmentConsent',
    label: 'Consent (Modafinil Therapy):',
    type: 'select',
    educationalSlide: MODAFINIL_THERAPY_CONSENT,
    answer: {
      type: 'single_select',
      options: [
        {
          value:
            'I have read the above information and I do not wish to continue',
          class: 'option-decline',
          key: 'do_not_continue'
        },
        {
          value:
            'I have read the above information and I do consent and wish to move forward',
          class: 'option-consent',
          key: 'consent'
        }
      ]
    }
  }
] as const;

export type ModafinilQuestionFieldAnswers = Partial<
  QuestionAnswerMap<typeof modafinilQuestions>
> &
  GenericTerminalQuestionFieldAnswers;

export default modafinilQuestions;
